/**
 * CardBinManagementPage.js — Panel CRUD del registro de BINs para CEO y
 * Provider CU. Permite agregar, editar bank_name, pausar o eliminar BINs
 * por moneda destino (CUP / MLC / USD).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Trash2, Plus, CreditCard, Pause, Play } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['CUP', 'MLC', 'USD'];

const CardBinManagementPage = () => {
  const { getAuthHeaders } = useAuth();
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ bin: '', currency: 'CUP', bank_name: '' });
  const [busy, setBusy] = useState(false);
  const [filterCcy, setFilterCcy] = useState('ALL');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/card-bins`, { headers: getAuthHeaders() });
      setBins(r.data.items || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar el registro'));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!/^[0-9]{4}$/.test(form.bin)) { toast.error('El BIN debe tener 4 dígitos numéricos'); return; }
    if (!form.bank_name) { toast.error('Indicá el banco'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/card-bins`, form, { headers: getAuthHeaders() });
      toast.success('BIN agregado');
      setOpenCreate(false);
      setForm({ bin: '', currency: 'CUP', bank_name: '' });
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo crear'));
    } finally { setBusy(false); }
  };

  const patchBin = async (id, patch, successMsg) => {
    try {
      await axios.patch(`${API}/card-bins/${id}`, patch, { headers: getAuthHeaders() });
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo actualizar'));
    }
  };

  const handleDelete = async (id, bin) => {
    if (!window.confirm(`¿Eliminar el BIN ${bin}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/card-bins/${id}`, { headers: getAuthHeaders() });
      toast.success(`BIN ${bin} eliminado`);
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo eliminar'));
    }
  };

  const filtered = filterCcy === 'ALL' ? bins : bins.filter((b) => b.currency === filterCcy);

  // Group by currency for clearer rendering.
  const grouped = CURRENCIES.map((c) => ({
    ccy: c,
    rows: filtered.filter((b) => b.currency === c),
  }));

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="card-bin-mgmt-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-emerald-600" />
            Registro de BINs
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Primeros 4 dígitos permitidos por moneda destino. Solo las tarjetas con BINs registrados y activos podrán recibir remesas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterCcy} onValueChange={setFilterCcy}>
            <SelectTrigger className="w-36" data-testid="filter-ccy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setOpenCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-bin-btn">
            <Plus className="w-4 h-4 mr-1" /> Agregar BIN
          </Button>
        </div>
      </div>

      {grouped.map((g) => g.rows.length > 0 && (
        <Card key={g.ccy} className="p-5 border-0 shadow-sm" data-testid={`bin-group-${g.ccy}`}>
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            🇨🇺 {g.ccy} <span className="text-xs text-slate-500 font-normal">· {g.rows.length} BIN{g.rows.length !== 1 ? 's' : ''}</span>
          </h3>
          <div className="space-y-2">
            {g.rows.map((b) => (
              <div key={b.id} className={`flex items-center gap-3 p-3 rounded-lg border ${b.is_paused ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50/40 border-slate-200'}`} data-testid={`bin-row-${b.bin}`}>
                <div className="font-mono text-lg font-bold text-slate-900 w-20">{b.bin}</div>
                <div className="flex-1">
                  <Input
                    value={b.bank_name}
                    onChange={(e) => {
                      const next = bins.map((x) => x.id === b.id ? { ...x, bank_name: e.target.value } : x);
                      setBins(next);
                    }}
                    onBlur={(e) => {
                      if (e.target.value !== b.bank_name) {
                        patchBin(b.id, { bank_name: e.target.value }, 'Banco actualizado');
                      }
                    }}
                    className="h-9 text-sm"
                    data-testid={`bin-bank-${b.bin}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {b.is_paused && <span className="text-[10px] uppercase bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">Pausado</span>}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => patchBin(b.id, { is_paused: !b.is_paused }, b.is_paused ? 'BIN reanudado' : 'BIN pausado')}
                    className={b.is_paused ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-600 hover:text-amber-700'}
                    data-testid={`bin-toggle-${b.bin}`}
                    title={b.is_paused ? 'Reactivar' : 'Pausar'}
                  >
                    {b.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(b.id, b.bin)}
                    className="text-rose-600 hover:text-rose-700"
                    data-testid={`bin-delete-${b.bin}`}
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {loading && <p className="text-center text-slate-500 py-8">Cargando…</p>}
      {!loading && filtered.length === 0 && <p className="text-center text-slate-500 py-8">No hay BINs registrados.</p>}

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Agregar BIN</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>BIN (4 dígitos)</Label>
              <Input value={form.bin} onChange={(e) => setForm({ ...form, bin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="9234" maxLength={4} data-testid="new-bin-input" />
            </div>
            <div>
              <Label>Moneda destino</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="new-ccy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Banco</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Bandec / BPA / Metropolitano…" data-testid="new-bank-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create-bin">
              {busy ? 'Guardando…' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CardBinManagementPage;
